<template>
    <!-- Quick extend: add time to one licence from the row's + button.
         If the package has a supplier, the supplier and a top-up amount are shown here and
         booked in the same step (like an automatic customer extension). -->
    <div class="modal fade" id="licenseExtendModal" tabindex="-1" aria-hidden="true" ref="modalEl">
        <div class="modal-dialog modal-dialog-centered w-[560px] max-w-[96vw]">
            <div class="modal-content" v-if="props.item">
                <div class="modal-header">
                    <h1 class="modal-title fs-5">Add time to license</h1>
                    <button ref="closeBtn" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body px-4">
                    <div class="text-sm mb-1">{{ props.item.customer_email }} · {{ props.item.modem_name }} · ports {{ props.item.proxy_http_port }}/{{ props.item.proxy_sock_port }}</div>
                    <div class="text-sm text-gray-500 mb-3">Currently expires {{ fmt(props.item.expired_date) }}</div>
                    <div class="flex gap-2 mb-3">
                        <button class="btn btn-sm" :class="pick === '1d' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1d'">+ 1 day</button>
                        <button class="btn btn-sm" :class="pick === '1w' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1w'">+ 1 week</button>
                        <button class="btn btn-sm" :class="pick === '1m' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1m'">+ 1 month</button>
                        <button class="btn btn-sm" :class="pick === 'custom' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = 'custom'">Custom</button>
                    </div>
                    <div v-if="pick === 'custom'" class="mb-3">
                        New expiry (date and time):
                        <VueDatePicker v-model="customDate" format="dd/MM/yyyy HH:mm" :min-date="new Date()" :clearable="false" :teleport="true"></VueDatePicker>
                    </div>
                    <div class="text-sm">New expiry: <b>{{ fmt(newExpiry) }}</b><span class="text-gray-500"> (counted from the current expiry, or from now if already passed)</span></div>

                    <!-- supplier of this package -->
                    <div v-if="supplierLoading" class="text-xs text-gray-500 mt-3">Checking the package supplier…</div>
                    <div v-else-if="supplier" class="mt-3 pt-3 border-top">
                        <div class="text-sm">
                            Supplier: <b>{{ supplier.name }}</b> <span class="text-gray-500">({{ supplier.email }})</span>
                            · cost <b>{{ costLine }}</b>
                            · balance ${{ money(supplier.balance) }}
                        </div>
                        <div class="flex items-center gap-3 mt-2">
                            <label class="text-sm whitespace-nowrap">Top up supplier (USD)</label>
                            <v-text-field v-model.number="supplierAmount" type="number" min="0" step="0.01" variant="outlined" density="compact" rounded="0" hide-details class="w-40" :disabled="noSupplierCredit"></v-text-field>
                        </div>
                        <label class="flex items-center gap-2 text-sm mt-2">
                            <input type="checkbox" v-model="noSupplierCredit" class="form-check-input shadow-none" style="height: 18px; width: 18px;" />
                            Don't credit the supplier
                        </label>
                    </div>
                    <div v-else class="text-xs text-gray-500 mt-3">This package has no supplier, so nothing is credited.</div>

                    <!-- charge the customer: off by default; the standard price for the period, editable -->
                    <div v-if="customer" class="mt-3 pt-3 border-top">
                        <label class="flex items-center gap-2 text-sm">
                            <input type="checkbox" v-model="chargeCustomer" class="form-check-input shadow-none" style="height: 18px; width: 18px;" />
                            Deduct from the customer's balance
                            <span class="text-gray-500">· {{ customer.email }} · balance ${{ money(customer.balance) }}<template v-if="priceLine"> · price {{ priceLine }}</template></span>
                        </label>
                        <template v-if="chargeCustomer">
                            <div class="flex items-center gap-3 mt-2">
                                <label class="text-sm whitespace-nowrap">Charge customer (USD)</label>
                                <v-text-field v-model.number="customerAmount" type="number" min="0" step="0.01" variant="outlined" density="compact" rounded="0" hide-details class="w-40"></v-text-field>
                                <span v-if="Number(customerAmount) > Number(customer.balance)" class="text-xs text-red-500">More than the customer's balance; the server will refuse.</span>
                            </div>
                            <div class="text-xs text-gray-500 mt-1">Pre-filled with the standard {{ pick === '1w' ? 'weekly' : pick === '1m' ? 'monthly' : 'daily' }} price of this package; change it if needed. Taken from the balance when you press Add time, with an EXTEND entry in their transactions.</div>
                        </template>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button type="button" class="btn btn-primary" :disabled="busy || (isCustom && !customDate)" @click="extend">
                        {{ busy ? 'Adding…' : buttonLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import { useAdminLicenseStore } from "@/stores"
import VueDatePicker from '@vuepic/vue-datepicker';

const props = defineProps({ item: { type: Object, default: null } });
const emit = defineEmits(['updated']);
const store = useAdminLicenseStore();

const modalEl = ref(null);
const closeBtn = ref(null);
const pick = ref('1d');
const customDate = ref(null);
const busy = ref(false);
const supplier = ref(null);
const supplierLoading = ref(false);
const supplierAmount = ref(0);
const customer = ref(null);          // licence owner + selling prices per period
const chargeCustomer = ref(false);   // tick box: deduct from the customer's balance
const noSupplierCredit = ref(false);   // tick box: no top-up and no transaction for the supplier
const customerAmount = ref(0);
const defaultCustom = () => new Date(Math.max(Date.now(), new Date(props.item?.expired_date).getTime() || 0) + 24 * 60 * 60 * 1000);
watch(() => props.item, () => { pick.value = '1d'; customDate.value = defaultCustom(); supplier.value = null; supplierAmount.value = 0; customer.value = null; chargeCustomer.value = false; customerAmount.value = 0; noSupplierCredit.value = false; }, { immediate: true });

const isCustom = computed(() => pick.value === 'custom');
const unit = computed(() => pick.value === '1d' ? 'days' : pick.value === '1w' ? 'weeks' : 'months');
const base = () => new Date(Math.max(Date.now(), new Date(props.item?.expired_date).getTime() || 0));
const newExpiry = computed(() => {
    if (!props.item) return null;
    if (isCustom.value) return customDate.value ? new Date(customDate.value) : null;
    const d = base();
    if (unit.value === 'months') d.setMonth(d.getMonth() + 1);
    else if (unit.value === 'weeks') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    return d;
});
// amount = the supplier's package cost for the period chosen (+1 day -> daily plan cost, etc.);
// custom dates use the daily cost times the days added
const costFor = (unitKey) => Number((supplier.value?.costs || {})[unitKey] ?? supplier.value?.cost_price ?? 0);
const suggested = computed(() => {
    if (!supplier.value) return 0;
    if (pick.value === '1d') return costFor('DAY');
    if (pick.value === '1w') return costFor('WEEK');
    if (pick.value === '1m') return costFor('MONTH');
    if (!newExpiry.value) return 0;
    const days = Math.max(0, Math.round((newExpiry.value.getTime() - base().getTime()) / 86400000));
    return Math.round(costFor('DAY') * days * 100) / 100;
});
watch(suggested, (v) => { supplierAmount.value = v; });
// customer side: the standard selling price for the period chosen (custom = daily price x days)
const priceFor = (unitKey) => Number((customer.value?.prices || {})[unitKey] ?? customer.value?.price ?? 0);
const suggestedCustomer = computed(() => {
    if (!customer.value) return 0;
    if (pick.value === '1d') return priceFor('DAY');
    if (pick.value === '1w') return priceFor('WEEK');
    if (pick.value === '1m') return priceFor('MONTH');
    if (!newExpiry.value) return 0;
    const days = Math.max(0, Math.round((newExpiry.value.getTime() - base().getTime()) / 86400000));
    return Math.round(priceFor('DAY') * days * 100) / 100;
});
watch(suggestedCustomer, (v) => { customerAmount.value = v; });
watch(chargeCustomer, (on) => { if (on) customerAmount.value = suggestedCustomer.value; });
const priceLine = computed(() => {
    const p = customer.value?.prices || {};
    return ['DAY', 'WEEK', 'MONTH'].filter(k => p[k] != null).map(k => '$' + money(p[k]) + '/' + periodWord(k)).join(' · ');
});
const buttonLabel = computed(() => {
    const parts = ['Add time'];
    if (chargeCustomer.value && Number(customerAmount.value) > 0) parts.push('− $' + money(customerAmount.value) + ' from customer');
    if (supplier.value && !noSupplierCredit.value && Number(supplierAmount.value) > 0) parts.push('+ $' + money(supplierAmount.value) + ' to ' + supplier.value.name);
    return parts.join(' ');
});
const costLine = computed(() => {
    const c = supplier.value?.costs || {};
    const parts = ['DAY', 'WEEK', 'MONTH'].filter(k => c[k] != null).map(k => '$' + money(c[k]) + '/' + periodWord(k) + (supplier.value?.custom?.[k] ? ' (custom commission)' : ''));
    return parts.length ? parts.join(' · ') : '$' + money(supplier.value?.cost_price);
});

const fmt = (d) => d ? new Date(d).toLocaleString() : '-';
const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodWord = (u) => ({ DAY: 'day', WEEK: 'week', MONTH: 'month' }[String(u || '').toUpperCase()] || 'period');

// on open: who supplies this package (if anyone)
async function loadSupplier() {
    if (!props.item?.uuid) return;
    supplierLoading.value = true;
    const info = await store.extendInfo(props.item.uuid);
    supplier.value = info.supplier; customer.value = info.customer;
    supplierLoading.value = false;
    supplierAmount.value = suggested.value;   // the package cost for the chosen period, editable
    customerAmount.value = suggestedCustomer.value;
}
onMounted(() => modalEl.value && modalEl.value.addEventListener('shown.bs.modal', loadSupplier));
onBeforeUnmount(() => modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', loadSupplier));

async function extend() {
    busy.value = true;
    const amount = supplier.value && !noSupplierCredit.value ? Math.max(0, Number(supplierAmount.value) || 0) : 0;
    const charge = chargeCustomer.value && customer.value ? Math.max(0, Number(customerAmount.value) || 0) : 0;
    const ok = await store.extendTime(props.item.uuid, 1, unit.value, isCustom.value ? new Date(customDate.value).toISOString() : null, amount, charge, !!supplier.value && noSupplierCredit.value);
    busy.value = false;
    if (!ok) return;   // time not added: stay open so the error can be read
    emit('updated');
    closeBtn.value?.click();   // added (a failed supplier top-up or customer charge is shown in its own message)
}
</script>
